import { Component, Input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { ButtonComponent } from '../button/button.component';
import { ButtonTypeEnum, ButtonSizeEnum } from '../button/buttonTypesEnum';
import {
  DropdownComponent,
  DropdownItem
} from '../dropdown/dropdown.component';
import { InputTextComponent } from '../input-text/input-text.component';
import { AppInputDateComponent } from '../input-date/input-date.component';

/**
 * Option for select-type search fields
 */
export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Search field option for dropdown
 */
export interface SearchFieldOption extends DropdownItem<string> {
  inputType: 'text' | 'date' | 'select';
  placeholder: string;
  selectOptions?: SelectOption[];
}

/**
 * Search event data emitted when search is triggered
 */
export interface SearchEvent {
  searchField: string;
  searchValue: string;
}

/**
 * SearchControlsComponent - Generic search form component
 *
 * Reusable component for search functionality across different modules
 * Allows selecting a search field and providing a value to search.
 *
 * @example
 * ```html
 * <app-search-controls
 *   [searchFieldOptions]="mySearchOptions"
 *   (searchExecuted)="handleSearch($event)"
 *   (clear)="handleClear()">
 * </app-search-controls>
 * ```
 */
@Component({
  selector: 'app-search-controls',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    DropdownComponent,
    InputTextComponent,
    AppInputDateComponent
  ],
  templateUrl: './search-controls.component.html',
  styleUrls: ['./search-controls.component.scss']
})
export class SearchControlsComponent {
  /** Expose enums to template */
  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  /**
   * Search field options to display in dropdown
   */
  @Input({ required: true }) searchFieldOptions!: SearchFieldOption[];

  /** Selected search field */
  readonly selectedField = signal<SearchFieldOption | null>(null);

  /** FormControl for text input */
  readonly textControl = new FormControl<string>('');

  /** FormControl for date input */
  readonly dateControl = new FormControl<Date | undefined>(undefined);

  /** Search value for text inputs (signal for reactivity) */
  readonly searchValueText = toSignal(
    this.textControl.valueChanges.pipe(
      startWith(''),
      map((value) => value ?? '')
    ),
    { initialValue: '' }
  );

  /** Search value for date inputs (signal for reactivity) */
  readonly searchValueDate = signal<Date | undefined>(undefined);

  /** Search value for select inputs (signal for reactivity) */
  readonly searchValueSelect = signal<string | null>(null);

  /**
   * Computed current input type based on selected field
   */
  readonly currentInputType = computed(() => {
    return this.selectedField()?.inputType ?? 'text';
  });

  /**
   * Computed select options for dropdown based on selected field
   */
  readonly currentSelectOptions = computed(() => {
    const field = this.selectedField();
    if (field?.inputType === 'select' && field.selectOptions) {
      return field.selectOptions.map((opt) => ({
        value: opt.value,
        label: opt.label
      }));
    }
    return [];
  });

  /**
   * Computed placeholder based on selected field
   */
  readonly currentPlaceholder = computed(() => {
    return (
      this.selectedField()?.placeholder ?? 'Seleccione un campo de búsqueda'
    );
  });

  /**
   * Signal: Whether search is currently active
   */
  private readonly _hasActiveSearch = signal(false);
  readonly hasActiveSearch = this._hasActiveSearch.asReadonly();

  /** Active search information for display */
  readonly activeSearchInfo = signal<string>('');

  /**
   * Output: Emitted when search is executed
   */
  searchExecuted = output<SearchEvent>();

  /**
   * Output: Emitted when clear is clicked
   */
  clear = output<void>();

  /**
   * Handle date change from app-input-date
   */
  onDateChange(date: Date | undefined): void {
    this.searchValueDate.set(date);
  }

  /**
   * Handle search field selection from dropdown
   * Clears input values when field changes
   */
  onFieldChange(value: string): void {
    const option = this.searchFieldOptions.find((opt) => opt.value === value);
    if (option) {
      this.selectedField.set(option);
      this.textControl.setValue('');
      this.dateControl.setValue(undefined);
      this.searchValueDate.set(undefined);
      this.searchValueSelect.set(null);
    }
  }

  /**
   * Handle select value change from secondary dropdown
   */
  onSelectValueChange(value: string): void {
    this.searchValueSelect.set(value);
  }

  /**
   * Execute search operation
   * Validates input and emits search event
   */
  executeSearch(): void {
    const field = this.selectedField();
    if (!field) {
      return;
    }

    const searchValue = this.getSearchValue(field);

    this._hasActiveSearch.set(true);
    this.activeSearchInfo.set(`${field.label}: ${searchValue}`);

    this.searchExecuted.emit({
      searchField: field.value,
      searchValue
    });
  }

  /**
   * Get search value based on input type
   */
  private getSearchValue(field: SearchFieldOption): string {
    switch (field.inputType) {
      case 'date':
        return this.formatDate(this.searchValueDate() ?? new Date());
      case 'select':
        return this.searchValueSelect() ?? '';
      default:
        return this.searchValueText().trim();
    }
  }

  /**
   * Clear active search
   * Resets all fields and emits clear event
   */
  clearSearch(): void {
    this.selectedField.set(null);
    this.textControl.setValue('');
    this.dateControl.setValue(undefined);
    this.searchValueDate.set(undefined);
    this.searchValueSelect.set(null);
    this._hasActiveSearch.set(false);
    this.activeSearchInfo.set('');

    this.clear.emit();
  }

  /** Reset visual state without emitting clear event. Called via ViewChild. */
  resetVisualState(): void {
    this.selectedField.set(null);
    this.textControl.setValue('');
    this.dateControl.setValue(undefined);
    this.searchValueDate.set(undefined);
    this.searchValueSelect.set(null);
  }

  /**
   * Format date to YYYY-MM-DD format for API
   * @param date Date to format
   * @returns Formatted date string
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
